// 全データのCSVエクスポート／インポート（CONTRACTS.md §3）
//
// フォーマット: categories / expenses / budgets の3セクションを
// `[section-name]` の見出し行 + ヘッダー行 + データ行 で構成する単一CSV文字列。
// 例:
//   [categories]
//   id,name,sortOrder,createdAt
//   ...
//   [expenses]
//   id,categoryId,date,amount,memo,paymentMethod,createdAt,updatedAt
//   ...
//   [budgets]
//   id,categoryId,yearMonth,amount
//   ...

import { db } from './db';
import type { Category, Expense, Budget } from './types';

const CATEGORIES_HEADER = ['id', 'name', 'sortOrder', 'createdAt'] as const;
const EXPENSES_HEADER = [
  'id',
  'categoryId',
  'date',
  'amount',
  'memo',
  'paymentMethod',
  'createdAt',
  'updatedAt',
] as const;
const BUDGETS_HEADER = ['id', 'categoryId', 'yearMonth', 'amount'] as const;

/** CSVフィールドとして安全な文字列にエスケープする（RFC4180準拠、区切りはカンマ） */
function escapeCsvField(value: unknown): string {
  const str = value === undefined || value === null ? '' : String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function toCsvLine(fields: readonly unknown[]): string {
  return fields.map(escapeCsvField).join(',');
}

/**
 * 1行分のCSVをパースしてフィールド配列を返す簡易パーサー（ダブルクォート・カンマ・改行対応）。
 */
function parseCsvLines(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === ',') {
      pushField();
      i++;
      continue;
    }
    if (ch === '\r') {
      i++;
      continue;
    }
    if (ch === '\n') {
      pushRow();
      i++;
      continue;
    }
    field += ch;
    i++;
  }
  // 末尾に未確定のフィールド/行が残っていれば確定させる
  if (field.length > 0 || row.length > 0) {
    pushRow();
  }
  // 完全な空行を除去
  return rows.filter((r) => !(r.length === 1 && r[0] === ''));
}

/** categories, expenses, budgets 全件をCSV文字列に変換して返す */
export async function exportAllToCsv(): Promise<string> {
  const [categories, expenses, budgets] = await Promise.all([
    db.categories.orderBy('sortOrder').toArray(),
    db.expenses.toArray(),
    db.budgets.toArray(),
  ]);

  const lines: string[] = [];

  lines.push('[categories]');
  lines.push(toCsvLine(CATEGORIES_HEADER));
  for (const c of categories) {
    lines.push(toCsvLine([c.id, c.name, c.sortOrder, c.createdAt]));
  }

  lines.push('[expenses]');
  lines.push(toCsvLine(EXPENSES_HEADER));
  for (const e of expenses) {
    lines.push(
      toCsvLine([
        e.id,
        e.categoryId,
        e.date,
        e.amount,
        e.memo,
        e.paymentMethod ?? '',
        e.createdAt,
        e.updatedAt,
      ])
    );
  }

  lines.push('[budgets]');
  lines.push(toCsvLine(BUDGETS_HEADER));
  for (const b of budgets) {
    lines.push(toCsvLine([b.id, b.categoryId, b.yearMonth, b.amount]));
  }

  return lines.join('\r\n');
}

type Section = 'categories' | 'expenses' | 'budgets' | null;

/**
 * CSV文字列から既存の全データ（categories, expenses, budgets）を置き換える。
 * 呼び出し側（SETTINGS）で確認ダイアログを経てから呼ぶこと。
 */
export async function importFromCsv(
  csvText: string
): Promise<{ categories: number; expenses: number; budgets: number }> {
  const rows = parseCsvLines(csvText);

  const categories: Category[] = [];
  const expenses: Expense[] = [];
  const budgets: Budget[] = [];

  let section: Section = null;
  let expectHeader = false;

  for (const row of rows) {
    if (row.length === 1 && /^\[.+\]$/.test(row[0])) {
      const name = row[0].slice(1, -1);
      if (name === 'categories' || name === 'expenses' || name === 'budgets') {
        section = name;
        expectHeader = true;
      } else {
        section = null;
      }
      continue;
    }

    if (expectHeader) {
      // ヘッダー行はスキップ（列順は固定フォーマットとして扱う）
      expectHeader = false;
      continue;
    }

    if (section === 'categories') {
      const [id, name, sortOrder, createdAt] = row;
      if (!id) continue;
      categories.push({
        id,
        name: name ?? '',
        sortOrder: Number(sortOrder) || 0,
        createdAt: createdAt ?? new Date().toISOString(),
      });
    } else if (section === 'expenses') {
      const [id, categoryId, date, amount, memo, paymentMethod, createdAt, updatedAt] = row;
      if (!id) continue;
      expenses.push({
        id,
        categoryId: categoryId ?? '',
        date: date ?? '',
        amount: Number(amount) || 0,
        memo: memo ?? '',
        paymentMethod: paymentMethod ? paymentMethod : undefined,
        createdAt: createdAt ?? new Date().toISOString(),
        updatedAt: updatedAt ?? new Date().toISOString(),
      });
    } else if (section === 'budgets') {
      const [id, categoryId, yearMonth, amount] = row;
      if (!id) continue;
      budgets.push({
        id,
        categoryId: categoryId ?? '',
        yearMonth: yearMonth ?? '',
        amount: Number(amount) || 0,
      });
    }
  }

  await db.transaction('rw', db.categories, db.expenses, db.budgets, async () => {
    await Promise.all([
      db.categories.clear(),
      db.expenses.clear(),
      db.budgets.clear(),
    ]);
    await Promise.all([
      db.categories.bulkPut(categories),
      db.expenses.bulkPut(expenses),
      db.budgets.bulkPut(budgets),
    ]);
  });

  return {
    categories: categories.length,
    expenses: expenses.length,
    budgets: budgets.length,
  };
}
